import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import SearchIcon from '@atlaskit/icon/core/search';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { CommandIcon } from '../../../../common/constants/icons/command';
import { ReturnIcon } from '../../../../common/constants/icons/return';
import { type PrimaryProductKey, PrimaryProductKeys } from '../../../../common/constants/products';
import { useAppContext } from '../../../../common/ui/app-context';
import { SearchFooter } from '../../../../common/ui/quick-find/search-dialog-footer';
import { type State } from '../../../../common/utils/quick-find/state';
import { isOnJiraFullPageSearch } from '../../../../common/utils/url';
import { useQuickFindAnalytics } from '../../../../controllers/quick-find/analytics';
import { type QuickFindFooterEvent } from '../../../../controllers/quick-find/utils';
import { usePrimaryProduct, useSearchActions } from '../../../../controllers/store';
import { useQuickFindQuery } from '../../../../controllers/store/quick-find';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { preloadSearchPageQuery } from '../../../search-page-relay/utils/query-loader';

import { messages } from './messages';
import { AllAppsConfluenceFirst, AllAppsJiraFirst } from './search-all-apps-icon';

type Props = {
	state: State;
	onClick: (props: QuickFindFooterEvent) => void;
};

const moreButtonStyles = xcss({
	alignItems: 'center',
	backgroundColor: 'color.background.neutral',
	borderRadius: token('radius.small'),
	display: 'flex',
	paddingLeft: 'space.100',
	paddingRight: 'space.075',
});

const allAppsIconsStyles = xcss({
	display: 'flex',
	translate: token('space.050'),
});

const AllAppsIconComponent = ({ product }: { product?: PrimaryProductKey }) => {
	const { formatMessage } = useIntl();
	return (
		<Flex role="presentation">
			<Box xcss={allAppsIconsStyles}>
				{product === PrimaryProductKeys.Jira ? <AllAppsJiraFirst /> : <AllAppsConfluenceFirst />}
			</Box>

			<Box xcss={moreButtonStyles}>
				<Text size="small" align="center" color="color.text.subtle">
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					{`+ ${formatMessage(messages.more)}`}
				</Text>
			</Box>
		</Flex>
	);
};

export const SearchPageLink = ({ state, onClick }: Props): React.JSX.Element => {
	const intl = useIntl();

	const { generateSearchUrl, queryParams } = useAppContext();

	const [primaryProduct] = usePrimaryProduct();
	const quickFindQuery = useQuickFindQuery();
	const { clearAllFilters } = useSearchActions();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const onResultClicked = useCallback(
		(event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>) => {
			onClick({
				state,
				event,
				actionSubjectId: `${primaryProduct?.toLowerCase() || ''}AdvancedSearchLink`,
			});

			// Prefetch FPS results
			preloadSearchPageQuery(
				{
					search: { searchQuery: quickFindQuery },
				},
				undefined,
				fireAnalyticsEvent,
			);

			// Executing a search query on FPS search will always clear the filters in QF as FPS filters
			// is the source of truth for filter state.
			clearAllFilters();
		},
		[onClick, state, primaryProduct, clearAllFilters, quickFindQuery, fireAnalyticsEvent],
	);

	const swapQuickFindCTAs =
		isOnJiraFullPageSearch() &&
		primaryProduct &&
		primaryProduct === PrimaryProductKeys.Jira &&
		fg('jira_search_fps_cta_swap');

	const icon = (
		<SearchIcon label={intl.formatMessage(messages.searchAcrossProducts)} color="currentColor" />
	);

	const title = intl.formatMessage(messages.searchAcrossProducts);

	const rightMetadata = (
		<Inline alignBlock="center" space="space.100">
			<AllAppsIconComponent product={primaryProduct} />
			{primaryProduct === PrimaryProductKeys.Jira && !swapQuickFindCTAs && <CommandIcon />}
			<ReturnIcon />
		</Inline>
	);

	const url = generateSearchUrl({
		...queryParams,
		text: quickFindQuery,
	});

	return (
		<SearchFooter
			icon={icon}
			title={title}
			rightMetadata={rightMetadata}
			url={url}
			onResultClicked={onResultClicked}
		/>
	);
};
